import { useEffect, useState } from 'react';
import { apiFetch } from '../api';

type PaymentStatus = {
  locked: boolean;
  warning: boolean;
  invoice?: {
    id: number;
    number: string;
    amount: number;
    due_date: string;
    days_overdue: number;
  };
};

type PaymentWarningPopupProps = {
  onClose: () => void;
};

export default function PaymentWarningPopup({ onClose }: PaymentWarningPopupProps) {
  const [status, setStatus] = useState<PaymentStatus | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  async function checkPaymentStatus() {
    try {
      const data = await apiFetch('/admin/invoices/payment-status/check');
      setStatus(data);
      setLoading(false);
      
      // Auto-close wenn keine Warnung
      if (!data.locked && !data.warning) {
        onClose();
      }
    } catch (e) {
      console.error('Payment status check failed:', e);
      setLoading(false);
      onClose();
    }
  }

  async function verifyPasswordAndClose() {
    setError(null);
    
    if (!password) {
      return setError('Bitte Passwort eingeben');
    }
    
    try {
      // Passwort über Login-Endpunkt verifizieren
      const username = localStorage.getItem('username');
      if (!username) {
        return setError('Benutzername nicht gefunden');
      }
      
      await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password })
      });
      
      onClose();
    } catch (e: any) {
      setError('Ungültiges Passwort');
    }
  }

  if (loading) {
    return null;
  }

  if (!status || (!status.locked && !status.warning)) {
    return null;
  }

  const isLocked = status.locked;
  const invoice = status.invoice;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-4">
      <div className={`${isLocked ? 'bg-red-600' : 'bg-orange-500'} text-white rounded-lg shadow-2xl max-w-2xl w-full p-8 relative`}>
        {/* Warnung Symbol */}
        <div className="text-center mb-6">
          <div className="text-8xl mb-4 animate-pulse">
            {isLocked ? '🔒' : '⚠️'}
          </div>
          <h2 className="text-4xl font-bold mb-2">
            {isLocked ? 'PORTAL GESPERRT' : 'ZAHLUNGSERINNERUNG'}
          </h2>
          <div className="text-xl">
            {isLocked ? 'Offene Rechnung überfällig' : 'Rechnung bald fällig'}
          </div>
        </div>

        {/* Rechnungsinformationen */}
        {invoice && (
          <div className="bg-white dark:bg-gray-800 bg-opacity-20 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-2 gap-4 text-lg">
              <div>
                <div className="font-semibold mb-1">Rechnungsnummer:</div>
                <div className="font-mono text-2xl">{invoice.number}</div>
              </div>
              <div>
                <div className="font-semibold mb-1">Betrag:</div>
                <div className="font-mono text-2xl">{Number(invoice.amount).toFixed(2)} €</div>
              </div>
              <div>
                <div className="font-semibold mb-1">Fälligkeitsdatum:</div>
                <div className="text-xl">{new Date(invoice.due_date).toLocaleDateString('de-DE')}</div>
              </div>
              <div>
                <div className="font-semibold mb-1">Überfällig seit:</div>
                <div className="text-2xl font-bold">{invoice.days_overdue} Tagen</div>
              </div>
            </div>
          </div>
        )}

        {/* Nachricht */}
        <div className="bg-white dark:bg-gray-800 bg-opacity-20 rounded-lg p-6 mb-6">
          <p className="text-lg leading-relaxed">
            {isLocked ? (
              <>
                <strong className="text-2xl block mb-3">Das Portal wurde aufgrund ausstehender Zahlungen gesperrt.</strong>
                Bitte überweisen Sie den offenen Betrag umgehend, um den Zugang wiederherzustellen.
                Bei Fragen kontaktieren Sie bitte unseren Support unter <strong>rechnung@vereinsos.de</strong>.
              </>
            ) : (
              <>
                <strong className="text-xl block mb-3">Ihre Rechnung ist bald fällig!</strong>
                Bitte begleichen Sie die offene Rechnung zeitnah, um eine Sperrung des Portals zu vermeiden.
                Bei Zahlungsschwierigkeiten kontaktieren Sie uns bitte frühzeitig.
              </>
            )}
          </p>
        </div>

        {/* Passwort-Eingabe für Schließen */}
        <div className="bg-white dark:bg-gray-800 bg-opacity-20 rounded-lg p-6">
          <div className="mb-4">
            <label className="block text-lg font-semibold mb-3">
              {isLocked 
                ? 'Administrator-Passwort eingeben um Einsicht zu erhalten:' 
                : 'Ihr Passwort eingeben um Warnung zu schließen:'}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verifyPasswordAndClose()}
              placeholder="Passwort"
              className="w-full px-4 py-3 text-lg border-2 border-white rounded bg-white dark:bg-gray-800 bg-opacity-90 text-gray-900 dark:text-white font-semibold focus:outline-none focus:ring-4 focus:ring-white"
              autoFocus
            />
          </div>

          {error && (
            <div className="bg-red-900 bg-opacity-60 text-white px-4 py-3 rounded mb-4 text-center font-semibold">
              {error}
            </div>
          )}

          <button
            onClick={verifyPasswordAndClose}
            className="w-full px-6 py-4 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700bg-gray-800 text-gray-900 dark:text-white rounded-lg font-bold text-lg transition transform hover:scale-105"
          >
            {isLocked ? '🔓 Einsicht erhalten' : '✓ Warnung bestätigt'}
          </button>
        </div>

        {/* Kontakt-Hinweis */}
        <div className="mt-6 text-center text-sm bg-white dark:bg-gray-800 bg-opacity-10 rounded p-4">
          <p>
            <strong>Support-Kontakt:</strong> rechnung@vereinsos.de | Tel: +49 123 456789
          </p>
        </div>
      </div>
    </div>
  );
}
